import React, { useMemo } from 'react'
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import * as UIcons from '@iconscout/react-unicons'

function ChartTooltip({ active, payload, label, valueFormatter }) {
  if (!active || !payload?.length) return null

  return (
    <div className="bg-white rounded-lg border border-border-lines shadow-lg px-3 py-2">
      <p className="text-sm font-semibold text-dark-blue">{label}</p>
      {
        payload.map((entry) => (
          <p key={entry.dataKey} className="text-sm text-secondary">
            {valueFormatter(entry.value)}
          </p>
        ))
      }
    </div>
  )
}

export default function MultiLineChartCard({
  title, subtitle, series, period = 'Daily', height = 260, tabs, activeTab, onTabChange, valueFormatter = (value) => value.toLocaleString(),
  className = 'w-full rounded-2xl border border-border-lines p-4'
}) {
  const data = useMemo(() => {
    if (!series.length) return []
    return series[0].data.map((point, index) => {
      const row = { label: point.label }
      series.forEach((line) => { row[line.key] = line.data[index]?.value ?? 0 })
      return row
    })
  }, [series])

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-base font-semibold text-dark-blue uppercase tracking-wide">{title}</p>
        <button type="button" className="flex items-center gap-1.5 bg-background rounded-full h-8 px-3 text-xs font-medium text-dark-blue">
          {period} <UIcons.UilAngleDown size={14} className="text-secondary" />
        </button>
      </div>

      {subtitle && <p className="text-sm text-secondary mb-3">{subtitle}</p>}

      {
        tabs?.length > 0 && (
          <div className="flex flex-wrap items-center gap-4 mb-3 border-b border-border-lines">
            {
              tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => onTabChange?.(tab.key)}
                  className={`pb-2.5 -mb-px text-sm font-medium border-b-2 whitespace-nowrap ${
                    tab.key === activeTab ? 'border-primary text-primary' : 'border-transparent text-secondary'
                  }`}
                >
                  {tab.label}
                </button>
              ))
            }
          </div>
        )
      }

      {
        series.length > 1 && (
          <div className="flex flex-wrap items-center gap-4 mb-3">
            {
              series.map((line) => (
                <div key={line.key} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: line.color }} />
                  <span className="text-xs text-secondary">{line.label}</span>
                </div>
              ))
            }
          </div>
        )
      }

      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#E6EBF0" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#748799' }} tickLine={false} axisLine={{ stroke: '#E6EBF0' }} />
            <YAxis tickFormatter={valueFormatter} tick={{ fontSize: 11, fill: '#748799' }} tickLine={false} axisLine={false} />
            <Tooltip content={<ChartTooltip valueFormatter={valueFormatter} />} cursor={{ stroke: '#B9C4D0', strokeDasharray: '3 3' }} />
            {
              series.map((line) => (
                <Line
                  key={line.key}
                  type="monotone"
                  dataKey={line.key}
                  name={line.label}
                  stroke={line.color}
                  strokeWidth={2}
                  dot={{ r: 3, strokeWidth: 0, fill: line.color }}
                  activeDot={{ r: 4 }}
                />
              ))
            }
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
